<template>
    <SettingsCard id="block-positions" class="ds-settings-block">
        <header class="ds-settings-card-header">
            <h2 class="ds-settings-card-title">
                Должности
                <span v-if="editor.load.positions === 'ready'" class="ds-settings-title-count">{{ editor.positions.value.length }}</span>
            </h2>
        </header>

        <SettingsBlockState
            :status="editor.load.positions"
            :rows="6"
            error-text="Не удалось загрузить должности или графики."
            @retry="editor.loadPositions()"
        >
            <div v-if="!editor.positions.value.length" class="ds-settings-empty">Должности не найдены.</div>
            <template v-else>
                <div class="ds-settings-card-toolbar">
                    <label class="ds-settings-search-field">
                        <SettingsIcon name="search" :size="16" :stroke-width="2" />
                        <input v-model="query" type="search" placeholder="Поиск должности" aria-label="Поиск должности" autocomplete="off">
                    </label>
                </div>
                <p v-if="!editor.canEditPositions.value" class="ds-settings-card-note">Редактирование должностей недоступно для вашей роли.</p>

                <div class="ds-settings-position-table" role="table" aria-label="Должности">
                    <div class="ds-settings-position-row ds-settings-param-head" role="row">
                        <span role="columnheader">Полное название</span>
                        <span role="columnheader">Короткое название</span>
                        <span role="columnheader">Тип графика</span>
                    </div>
                    <div v-if="!visiblePositions.length" class="ds-settings-empty">Ничего не найдено</div>
                    <div
                        v-for="position in visiblePositions"
                        :id="`position-${position.id}`"
                        :key="position.id"
                        class="ds-settings-position-row"
                        :class="{
                            'is-changed': editor.changedAnchors.value.has(`position-${position.id}`),
                            'is-highlighted': editor.highlightedAnchor.value === `position-${position.id}`,
                        }"
                        role="row"
                    >
                        <span class="ds-settings-position-name" role="cell">{{ position.name }}</span>
                        <span role="cell">
                            <label class="ds-settings-number ds-settings-short-name" :class="{ 'is-changed': isShortChanged(position.id), 'is-disabled': disabled }">
                                <input
                                    class="ds-settings-number-input"
                                    type="text"
                                    :value="editor.positionValue(position.id).short_name ?? ''"
                                    :maxlength="POSITION_SHORT_NAME_MAX"
                                    placeholder="—"
                                    :disabled="disabled"
                                    :aria-label="`Короткое название: ${position.name}`"
                                    @input="editor.setPosition(position.id, 'short_name', ($event.target as HTMLInputElement).value)"
                                >
                            </label>
                        </span>
                        <span role="cell" class="ds-settings-position-schedule" :class="{ 'is-changed': isScheduleChanged(position.id) }">
                            <dropdown
                                :model-value="scheduleOption(position.id)"
                                :options="editor.shiftSchedules.value"
                                :disabled="disabled"
                                @update:model-value="editor.setPosition(position.id, 'shift_schedule', $event?.id ?? null)"
                            >
                                {{ scheduleOption(position.id)?.name || 'Выберите график' }}
                            </dropdown>
                        </span>
                    </div>
                </div>
            </template>
        </SettingsBlockState>
    </SettingsCard>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import SettingsCard from '@/ui/SettingsCard.vue'
import SettingsBlockState from './SettingsBlockState.vue'
import SettingsIcon from './SettingsIcon.vue'
import { useSettingsEditor } from './useSettingsEditor'
import { POSITION_SHORT_NAME_MAX } from './settingsModel'

const editor = useSettingsEditor()
const query = ref('')

const disabled = computed(() => !editor.canEditPositions.value || editor.saving.value)

// Фильтр только скрывает строки: их правки остаются в панели и сохраняются.
const visiblePositions = computed(() => {
    const normalized = query.value.trim().toLocaleLowerCase('ru')
    if (!normalized) return editor.positions.value
    return editor.positions.value.filter((position) => {
        const shortName = editor.positionValue(position.id).short_name ?? ''
        return position.name.toLocaleLowerCase('ru').includes(normalized)
            || shortName.toLocaleLowerCase('ru').includes(normalized)
    })
})

const changeIds = computed(() => new Set(editor.currentChanges.value.map((change) => change.id)))
const isShortChanged = (id: string) => changeIds.value.has(`position:${id}:short_name`)
const isScheduleChanged = (id: string) => changeIds.value.has(`position:${id}:shift_schedule`)

const scheduleOption = (id: string) => {
    const scheduleId = editor.positionValue(id).shift_schedule
    return editor.shiftSchedules.value.find((schedule) => schedule.id === scheduleId)
}
</script>
